import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const KEY = "ri:admin-token";
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem(KEY)}` } });

const Login = ({ onDone }) => {
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { const r = await api.post("/auth/login", f); sessionStorage.setItem(KEY, r.data.token); onDone(); }
    catch (x) { setErr(typeof x.response?.data?.detail === "string" ? x.response.data.detail : "Login failed"); }
  };
  return (
    <form onSubmit={submit} className="mx-auto mt-16 max-w-sm space-y-4 rounded-3xl border border-slate-200 bg-white p-6" data-testid="admin-login-form">
      <h1 className="text-2xl font-bold text-slate-900">Admin sign in</h1>
      <input type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="admin-email" className="w-full rounded-xl border border-slate-300 px-3 py-2" />
      <input type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required data-testid="admin-password" className="w-full rounded-xl border border-slate-300 px-3 py-2" />
      {err && <p className="text-sm text-rose-700" data-testid="admin-login-error">{err}</p>}
      <button className="btn-green w-full rounded-full py-2.5 text-sm font-semibold" data-testid="admin-login-submit">Sign in</button>
    </form>
  );
};

const ClaimCard = ({ c, onDecide }) => (
  <article className="rounded-3xl border border-slate-200 bg-white p-5" data-testid={`claim-card-${c.id}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <Link to={`/organizations/${c.org_slug}`} className="text-lg font-semibold text-slate-900 hover:underline">{c.org_name}</Link>
      <span className="text-xs text-slate-500">{new Date(c.created_at).toLocaleString("en-CA")}</span>
    </div>
    <p className="mt-1 text-sm text-slate-700">{c.contact_name} · {c.contact_role} · <a href={`mailto:${c.contact_email}`} className="text-sky-800">{c.contact_email}</a></p>
    {c.message && <p className="mt-2 text-sm italic text-slate-600">“{c.message}”</p>}
    <table className="mt-3 w-full text-left text-sm"><tbody>
      {Object.entries(c.changes).map(([k, val]) => (
        <tr key={k} className="border-t border-slate-100 align-top"><td className="py-2 pr-3 font-medium text-slate-800">{k.replaceAll("_", " ")}</td>
          <td className="py-2 pr-3 text-slate-500 line-through break-all">{c.current?.[k] || "—"}</td><td className="py-2 text-emerald-800 break-all">{val ?? "Remove link"}</td></tr>
      ))}
      {!Object.keys(c.changes).length && <tr><td className="py-2 text-slate-600">No link changes proposed.</td></tr>}
    </tbody></table>
    {c.status === "pending" ? (
      <div className="mt-4 flex gap-2">
        <button onClick={() => onDecide(c.id, "approve")} className="btn-green rounded-full px-5 py-2 text-sm font-semibold" data-testid={`claim-approve-${c.id}`}>Approve</button>
        <button onClick={() => onDecide(c.id, "reject")} className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-800" data-testid={`claim-reject-${c.id}`}>Reject</button>
      </div>
    ) : <p className="mt-3 text-xs text-slate-600">{c.status} by {c.decided_by} · {c.decided_at && new Date(c.decided_at).toLocaleString("en-CA")}</p>}
  </article>
);

export default function AdminClaims() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(KEY));
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    setRows(null);
    api.get("/admin/claims", { params: { status }, ...auth() }).then((r) => setRows(r.data))
      .catch((e) => { if (e.response?.status === 401) { sessionStorage.removeItem(KEY); setAuthed(false); } else setRows([]); });
  }, [status]);
  useEffect(() => { if (authed) load(); }, [authed, load]);
  const decide = async (id, action) => { await api.post(`/admin/claims/${id}`, { action }, auth()); load(); };
  if (!authed) return <div className="px-5"><Login onDone={() => setAuthed(true)} /></div>;
  return (
    <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8" data-testid="admin-claims-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Organization claims</h1>
        <button onClick={() => { sessionStorage.removeItem(KEY); setAuthed(false); }} className="text-sm text-slate-600 underline" data-testid="admin-logout">Sign out</button>
      </div>
      <div className="mt-5 flex gap-2">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} aria-pressed={status === s} data-testid={`claims-tab-${s}`}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold capitalize ${status === s ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-300 text-slate-700"}`}>{s}</button>
        ))}
      </div>
      <div className="mt-6 space-y-4">
        {rows === null && <div className="h-40 animate-pulse rounded-3xl bg-white" />}
        {rows?.length === 0 && <p className="text-slate-600" data-testid="claims-empty">No {status} claims.</p>}
        {rows?.map((c) => <ClaimCard key={c.id} c={c} onDecide={decide} />)}
      </div>
    </div>
  );
}
